import { useEffect, useState, type JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BLOOD_TYPES, MBTI_DESCRIPTIONS, type ProfilePersonal } from '@dxtech/shared';
import { Modal, Spinner } from '../ui/common.js';
import { api } from '../api/client.js';
import { personalApi } from '../api/personal.js';
import { MbtiQuiz } from './ProfileModal.js';
import { useApp } from '../state/app.js';

/** 본인 정보 수정 — 생일(양/음력)·주소·연락처·혈액형·MBTI */
export function MyProfileModal({ onClose }: { onClose: () => void }): JSX.Element {
  const { flash } = useApp();
  const qc = useQueryClient();
  const prof = useQuery({ queryKey: ['me', 'profile'], queryFn: () => api.meProfile() });
  const career = useQuery({ queryKey: ['profile', 'me'], queryFn: () => api.profile() }); // 혈액형·MBTI 는 개인 프로파일 파일에 있음

  const [birthday, setBirthday] = useState('');
  const [birthdayType, setBirthdayType] = useState<'양력' | '음력'>('양력');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [bloodType, setBloodType] = useState<ProfilePersonal['bloodType']>('');
  const [mbti, setMbti] = useState('');
  const [quiz, setQuiz] = useState(false);
  const [initP, setInitP] = useState(false);
  const [busy, setBusy] = useState(false);
  const [init, setInit] = useState(false);

  useEffect(() => {
    const p = prof.data?.profile;
    if (p && !init) {
      setBirthday(p.birthday ?? '');
      setBirthdayType(p.birthdayType ?? '양력');
      setAddress(p.address ?? '');
      setPhone(p.phone ?? '');
      setInit(true);
    }
  }, [prof.data, init]);
  useEffect(() => {
    if (!career.data || initP) return;
    const p = career.data.profile as ProfilePersonal | null;
    setBloodType(p?.bloodType ?? '');
    setMbti(p?.mbti ?? '');
    setInitP(true);
  }, [career.data, initP]);

  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.updateMyProfile({ birthday: birthday || undefined, birthdayType, address, phone });
      await personalApi.save({ bloodType: bloodType ?? '', mbti });
      qc.invalidateQueries({ queryKey: ['personal'] });
      qc.invalidateQueries({ queryKey: ['profile'] });
      flash('내 정보가 저장되었습니다');
      qc.invalidateQueries({ queryKey: ['me', 'profile'] });
      qc.invalidateQueries({ queryKey: ['board'] });
      qc.invalidateQueries({ queryKey: ['employees'] });
      onClose();
    } catch (e) {
      flash(e instanceof Error ? e.message : '저장 실패');
      setBusy(false);
    }
  };

  return (
    <Modal width={440} onClose={onClose}>
      <div style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>내 정보 수정</div>
          <button title="닫기" onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6B7280', fontSize: 26, lineHeight: 1 }}>×</button>
        </div>

        {prof.isLoading || career.isLoading ? (
          <div style={{ padding: 30, textAlign: 'center' }}><Spinner /></div>
        ) : (
          <>
            <div style={{ marginBottom: 14 }}>
              <div style={lbl}>생일</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="date" className="dx-input" style={{ flex: 1 }} value={birthday} onChange={(e) => setBirthday(e.target.value)} />
                <div style={{ display: 'flex', background: '#F3F4F6', borderRadius: 7, padding: 3 }}>
                  {(['양력', '음력'] as const).map((t) => (
                    <button key={t} onClick={() => setBirthdayType(t)} style={seg(birthdayType === t)}>{t}</button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={lbl}>연락처</div>
              <input className="dx-input" style={{ width: '100%' }} placeholder="010-0000-0000" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={lbl}>주소</div>
              <input className="dx-input" style={{ width: '100%' }} placeholder="주소" value={address} onChange={(e) => setAddress(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 6 }}>
              <div style={{ width: 120 }}>
                <div style={lbl}>혈액형</div>
                <select className="dx-select" style={{ width: '100%' }} value={bloodType ?? ''} onChange={(e) => setBloodType(e.target.value as ProfilePersonal['bloodType'])}>
                  <option value="">선택 안 함</option>
                  {BLOOD_TYPES.map((t) => <option key={t} value={t}>{t}형</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <div style={lbl}>MBTI</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <select className="dx-select" style={{ flex: 1 }} value={mbti} onChange={(e) => setMbti(e.target.value)}>
                    <option value="">선택 안 함</option>
                    {Object.keys(MBTI_DESCRIPTIONS).sort().map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <button className="dx-btn dx-btn-ghost" style={{ whiteSpace: 'nowrap' }} onClick={() => setQuiz(true)}>확인하기</button>
                </div>
              </div>
            </div>
            {mbti && MBTI_DESCRIPTIONS[mbti] && <div style={{ fontSize: 12, color: '#6B7280' }}>{mbti} · {MBTI_DESCRIPTIONS[mbti]}</div>}

            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button className="dx-btn dx-btn-ghost" style={{ flex: 1 }} onClick={onClose}>취소</button>
              <button className="dx-btn dx-btn-primary" style={{ flex: 1 }} disabled={busy} onClick={save}>저장</button>
            </div>
          </>
        )}
      </div>
      {quiz && <MbtiQuiz onClose={() => setQuiz(false)} onApply={(t) => { setMbti(t); setQuiz(false); flash(`MBTI를 ${t}(으)로 입력했습니다. 저장을 눌러 등록하세요.`); }} />}
    </Modal>
  );
}

const lbl = { fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6 } as const;
const seg = (active: boolean) => ({
  border: 'none', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, padding: '6px 12px', borderRadius: 5, cursor: 'pointer',
  background: active ? '#fff' : 'transparent', color: active ? '#1D4ED8' : '#6B7280',
  boxShadow: active ? '0 1px 2px rgba(16,24,40,.12)' : 'none',
} as const);
